import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

interface Context {
  id: string;
  text: string;
}

// Interface for the search result
interface SearchResult {
  id: string;
  text: string;
  context: Context[];
}

// Refactor result type
interface RefactorResult {
  text: string;
  pincode: number;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
// @ts-nocheck
export const refactor = (
  searchData: {
    context: {
      id: string;
      text: string;
    }[];
  }[]
) => {
  const arr: { text: string; pincode: number; id: string }[] = [];

  searchData?.map((data) => {
    const context = data.context;
    const city: string =
      context.filter((item: { id: string; text: string }) =>
        item.id.includes("place")
      )[0]?.text ?? "";
    const state: string =
      context.filter((item: { id: string; text: string }) =>
        item.id.includes("region")
      )[0]?.text ?? "";
    const pincode: number = parseInt(
      context.filter((item) => item.id.includes("postcode"))[0]?.text ?? "0"
    );

    if (city && state && pincode) {
      arr.push({
        text: `${city},${state},${pincode}`,
        pincode: pincode,
        id: JSON.stringify(pincode),
      });
    }
  });
  return arr;
};

// @ts-nocheck
export const refactorAffordability = (
  searchData: {
    context: { id: string; text: string }[];
    geometry: { coordinates: [number, number]; type: string };
  }[]
) => {
  const arr: {
    text: string;
    pincode: number;
    id: string;
    geometry: { coordinates: [number, number]; type: string };
  }[] = [];

  searchData?.forEach((data) => {
    const context = data.context;

    const city: string =
      context.filter((item: { id: string; text: string }) =>
        item.id.includes("place")
      )[0]?.text ?? "";
    const state: string =
      context.filter((item: { id: string; text: string }) =>
        item.id.includes("region")
      )[0]?.text ?? "";
    const pincode: number = parseInt(
      context.filter((item) => item.id.includes("postcode"))[0]?.text ?? "0"
    );

    if (city && state && pincode) {
      arr.push({
        text: `${city},${state},${pincode}`,
        pincode: pincode,
        id: JSON.stringify(pincode),
        geometry: data.geometry, // Include geometry for coordinates
      });
    }
  });
  return arr;
};

export function formatPropertyName(propertyName: string): string {
  // trim() + strip leading/trailing hyphens so a DB name with a stray
  // leading/trailing space (e.g. "Dunearn Road GLS ") does NOT produce a
  // slug with a dangling hyphen ("dunearn-road-gls-"). That dangling hyphen
  // made each affected page canonicalise to a different URL than the one
  // crawled, so Google skipped it as "Alternative page with proper canonical
  // tag". Mirrors formatProjectName, which already strips edge hyphens.
  return propertyName
    .replace(/-/g, " ")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase()
    .replace(/^-+|-+$/g, "");
}

export const formatCurrency = (value: number) => {
  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0, // Ensures no decimal places if not needed
    maximumFractionDigits: 2, // Allows up to 2 decimal places when necessary
  }).format(value);

  return formattedValue.replace("$", ""); // Remove "$" if needed
};

// S3 image folders are keyed off the project name. When a project is renamed
// but its images have not yet been re-uploaded under the new name (we have no
// S3 write access), alias the new slug back to the original folder so the
// existing images keep resolving. Remove an entry once real assets are
// uploaded under the new name. NOTE: this only affects the image folder
// (formatProjectName), not the URL slug (formatPropertyName), so canonical
// URLs stay on the new name.
const IMAGE_FOLDER_ALIASES: Record<string, string> = {
  "wynwood-grand": "woodlands-drive-17-ec",
  "solano-grand": "senja-close-gls-ec",
};

export const formatProjectName = (projectName: string): string => {
  // Preserve @ and ' since the historical S3 folder convention keeps them
  // (e.g. sky-eden@bedok/, the-reef-at-king's-dock/). Treat everything else
  // (spaces, hyphens, punctuation, CJK) as separators.
  const base = projectName
    .toLowerCase()
    .replace(/[^a-z0-9'@]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return IMAGE_FOLDER_ALIASES[base] ?? base;
};

export const generateImageURL = (
  projectName: string,
  imageType: string,
  imageName: string
): string => {
  if (!projectName || !imageType || !imageName) {
    return "";
  }
  return `${process.env.NEXT_PUBLIC_IMAGE_URL}/${formatProjectName(
    projectName
  )}/${imageType}/${imageName}`;
};

export const formatConstructionStatus = (constructionStatus: string) => {
  switch (constructionStatus) {
    case "Sold Out":
      return {
        status: "Sold Out",
        bg: "#DB1918",
      };
    case "Upcoming":
      return {
        status: "Upcoming",
        bg: "#0094FF",

      };
    case "Under Construction":
      return {
        status: "Under Construction",
        bg: "#22C55E",
      };
    case "Move In":
      return {
        status: "Move In",
        bg: "#22C55E",
      };
    default:
      return {
        status: constructionStatus,
        bg: "#0094FF",
      };
  }
};

export const removeHTMLTags = (html: string) => {
  html = html.replace(/&nbsp;/g, "");
  return html.replace(/<[^>]*>/g, "");
};

/**
 * Strip a leading heading element (h1/h2/h3) from `body` if its text
 * content matches `title` (case-insensitive, whitespace-collapsed,
 * trailing punctuation ignored).
 *
 * Why: blog page template already renders <h1>{title}</h1>. When the
 * admin pastes the article body and it begins with the same heading,
 * the title appears twice visually and Google sees two H1s — bad for
 * SEO and ugly UX. This strips the duplicate at render time so we
 * don't have to re-edit every existing post in the admin.
 */
export const stripDuplicateTitleHeading = (
  body: string,
  title: string,
): string => {
  if (!body || !title) return body;
  // Match a leading h1/h2/h3 (with optional preceding whitespace).
  const match = body.match(/^\s*<(h[1-3])\b[^>]*>([\s\S]*?)<\/\1>/i);
  if (!match) return body;
  const norm = (s: string) =>
    s
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/[.!?:;,\-–—"'""'']+$/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  if (norm(match[2]) === norm(title)) {
    return body.slice(match[0].length);
  }
  return body;
};


/**
 * Extract FAQ question/answer pairs from a Quill-authored article body.
 *
 * Most posts end with a "Common questions ..." H2 followed by repeated
 * <h3>Question</h3><p>Answer</p> blocks. This parses that section so the
 * blog page can emit FAQPage JSON-LD (Google rich results + AI Overview
 * citation) without anyone hand-authoring schema in the admin.
 *
 * Returns [] when no FAQ section is found, so callers can conditionally
 * render the schema.
 */
export const extractArticleFaqs = (
  body: string,
): Array<{ question: string; answer: string }> => {
  if (!body) return [];
  // Locate the FAQ section heading ("Common questions", "Frequently asked").
  const heading = body.match(
    /<h2[^>]*>(?:(?!<\/h2>)[\s\S])*?(?:common questions|frequently asked)(?:(?!<\/h2>)[\s\S])*?<\/h2>/i,
  );
  if (!heading) return [];
  let zone = body.slice(heading.index! + heading[0].length);
  // FAQ isn't always the last section — stop at the next H2 if present.
  const nextH2 = zone.search(/<h2[\s>]/i);
  if (nextH2 >= 0) zone = zone.slice(0, nextH2);

  const faqs: Array<{ question: string; answer: string }> = [];
  const qre = /<h3[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h3[\s>]|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = qre.exec(zone)) !== null) {
    const question = removeHTMLTags(m[1]).replace(/\s+/g, " ").trim();
    const answer = removeHTMLTags(m[2]).replace(/\s+/g, " ").trim();
    if (question && answer) faqs.push({ question, answer });
  }
  return faqs;
};

// lib/utils.ts
export const sortPropertiesByStatus = (properties: Property[]): Property[] => {
  const statusOrder: { [key: string]: number } = {
    Upcoming: 1,
    "Under Construction": 2,
    "Move In": 3, // Assuming "Completed" maps to "Move In"
    "Sold Out": 4,
  };

  return [...properties].sort((a, b) => {
    const statusA = a.construction_status || "Sold Out"; // Fallback to Sold Out if undefined
    const statusB = b.construction_status || "Sold Out";
    return (statusOrder[statusA] || 5) - (statusOrder[statusB] || 5); // Default to 5 for unknown statuses
  });
};